import { useEffect,useRef,useState } from "react";
import { useReadingApi } from "./api";
import { speakBrowser } from "./browser-speech";
import { playQuestionAudio,prepareQuestionAudio,type PreparedQuestionAudio } from "./question-audio";
import type { Language,Question } from "./types";
import { InlineIcon,NextButton,SPEECH_SUPPORTED } from "./ui";
type QuizProps = {
  text: string;
  language: Language;
  questions: Question[];
  onNewStory: () => void;
};

export function ComprehensionQuiz({ text, language, questions, onNewStory }: QuizProps) {
  const { fetch } = useReadingApi();
  const total = questions.length;
  const [index, setIndex] = useState(0);
  const [wrongPicks, setWrongPicks] = useState<number[]>([]);
  const [solved, setSolved] = useState(false);
  const [done, setDone] = useState(false);
  // loading covers the token request and Gemini's generation, before any sound is heard.
  const [audioState, setAudioState] = useState<"idle" | "loading" | "playing">("idle");
  const [audioError, setAudioError] = useState(false);
  const stopAudio = useRef<(() => void) | null>(null);
  const preparedAudio = useRef<PreparedQuestionAudio | null>(null);
  const current = questions[index];
  const options = current.format === "closed" ? current.options : [];
  const isLast = index + 1 >= total;
  const progress = done ? 100 : (index + (solved ? 1 : 0)) / total * 100;
  const feedback = current.format !== "closed"
    ? ""
    : solved
      ? "נכון! כל הכבוד."
      : wrongPicks.length
        ? current.hint
        : "";

  useEffect(() => () => stopAudio.current?.(), []);

  // Gemini starts reading as soon as the question shows, so the tap plays the finished recording.
  useEffect(() => {
    // Questions are Hebrew even when the reading text is English.
    const prepared = prepareQuestionAudio({ text, question: current.question, options, language: "he" }, fetch);
    preparedAudio.current = prepared;
    return () => {
      preparedAudio.current = null;
      prepared.cancel();
    };
  }, [index]);

  useEffect(() => {
    const target = document.querySelector<HTMLElement>(".reading-root .quiz-card, .reading-root .quiz .finish-note");
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: "start" });
  }, [index, done]);

  function choose(option: number) {
    if (solved) return;
    stopAudio.current?.();
    if (option === current.correctIndex) setSolved(true);
    else setWrongPicks((picks) => (picks.includes(option) ? picks : [...picks, option]));
  }

  function next() {
    stopAudio.current?.();
    if (isLast) {
      setDone(true);
      return;
    }
    setIndex(index + 1);
    setWrongPicks([]);
    setSolved(false);
  }

  function speakQuestion() {
    if (stopAudio.current) {
      stopAudio.current();
      return;
    }
    setAudioState("loading");
    setAudioError(false);
    stopAudio.current = playQuestionAudio(
      { text, question: current.question, options, language: "he" },
      (onEnd) => {
        if (!SPEECH_SUPPORTED) throw new Error("speech_unavailable");
        return speakBrowser([current.question, ...options], "he", onEnd);
      },
      (failed) => {
        stopAudio.current = null;
        setAudioState("idle");
        if (failed) setAudioError(true);
      },
      () => setAudioState("playing"),
      preparedAudio.current ?? undefined,
      fetch,
    );
  }

  return (
    <div className="quiz">
      <article className="paper">
        <p
          className="reading-text"
          aria-label="טקסט לקריאה"
          lang={language}
          dir={language === "en" ? "ltr" : "rtl"}
        >
          {text}
        </p>

        {done ? (
          <div className="finish-note" role="status" tabIndex={-1}>
            <p className="finish-note__text">
              <span aria-hidden="true">🎉</span> סיימתם את השאלות! כל הכבוד.
            </p>
          </div>
        ) : (
          <section className="quiz-card" key={index} aria-labelledby="quiz-question" tabIndex={-1}>
            <p className="quiz-card__counter">שאלה {index + 1} מתוך {total}</p>
            <h2 className="quiz-card__question" id="quiz-question" lang="he" dir="rtl">
              {current.question}
            </h2>

            {current.format === "closed" ? (
              <div className="quiz-options" role="group" aria-label="תשובות לבחירה">
                {current.options.map((option, optionIndex) => {
                  const isCorrect = solved && optionIndex === current.correctIndex;
                  const isWrong = wrongPicks.includes(optionIndex);
                  return (
                    <button
                      className={`quiz-option ${isCorrect ? "is-correct" : ""} ${isWrong ? "is-wrong" : ""}`}
                      type="button"
                      key={option}
                      disabled={solved || isWrong}
                      onClick={() => choose(optionIndex)}
                    >
                      <span className="quiz-option__mark" aria-hidden="true">
                        {isCorrect ? "✓" : isWrong ? "✗" : ""}
                      </span>
                      <span dir="auto">{option}</span>
                    </button>
                  );
                })}
              </div>
            ) : solved ? (
              <div className="quiz-answer" aria-live="polite">
                <p className="quiz-answer__label">התשובה</p>
                <p className="quiz-answer__text" dir="auto">{current.expectedAnswer}</p>
                {current.acceptableAnswers.length > 0 && (
                  <p className="quiz-answer__more">
                    אפשר גם: {current.acceptableAnswers.join(" / ")}
                  </p>
                )}
              </div>
            ) : (
              <div className="quiz-open">
                <p className="quiz-open__prompt">אמרו את התשובה בקול, ואז הציצו בתשובה.</p>
                <button className="quiz-reveal" type="button" onClick={() => setSolved(true)}>
                  <InlineIcon name="reveal" />
                  <span>הצגת התשובה</span>
                </button>
              </div>
            )}

            {feedback && (
              <p
                className={`quiz-feedback ${solved ? "quiz-feedback--correct" : "quiz-feedback--retry"}`}
                role="status"
              >
                {feedback}
              </p>
            )}
          </section>
        )}

      </article>

      <footer className="reader-footer">
        <div className="reader-footer__panel">
          <div className="reader-footer__inner">
            <div className="reader-footer__meta reader-footer__meta--single">
              <button
                className={`dock-button ${audioState === "loading" ? "dock-button--loading" : ""}`}
                type="button"
                aria-label={audioState === "idle" ? "השמעת השאלה" : "עצירת ההשמעה"}
                aria-pressed={audioState !== "idle"}
                aria-busy={audioState === "loading"}
                disabled={done}
                onClick={speakQuestion}
              >
                <InlineIcon name="volume" />
                <span>{audioState === "idle" ? "השמע" : audioState === "loading" ? "מכינים" : "עצור"}</span>
                {audioState === "loading" && <span className="dock-button__dots" aria-hidden="true">•••</span>}
              </button>
              {audioError && <span role="status">ההשמעה לא הצליחה. נסו שוב.</span>}
            </div>

            <div className="reader-footer__actions">
              <NextButton
                done={done}
                label={done ? "טקסט חדש" : isLast ? "סיום" : "לשאלה הבאה"}
                icon={done ? "refresh" : "next"}
                progress={progress}
                disabled={!done && !solved}
                onClick={done ? onNewStory : next}
              />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

